/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 83): signals from the call to the iON app that hosts it,
 * over the widget API like Element Call's own "im.vector.hangup":
 *   chat.ion.open_chat   { room_id }  "Chat": minimise the call, open its chat
 *   chat.ion.minimize    {}           minimise the call to a floating window
 *   chat.ion.add_people  { room_id }  "Add" / "Invite to call"
 * The app lists the ones it handles in the widget URL, e.g.
 * ionHostActions=open_chat,minimize,add_people; a button whose signal the
 * host does not handle is not shown (the web, older apps).
 * Described for the apps in ion-spec calls/CALL-SCREENS.md.
 */

import { createContext, type FC, type ReactNode, use } from "react";
import { logger as rootLogger } from "matrix-js-sdk/lib/logger";

import { type WidgetHelpers } from "../widget";

/** The signals to the app; false shows no such buttons anywhere. */
export const ION_HOST_ACTIONS: boolean = true;

export const HOST_ACTIONS = {
  open_chat: "chat.ion.open_chat",
  minimize: "chat.ion.minimize",
  add_people: "chat.ion.add_people",
} as const;

export type IonHostAction = keyof typeof HOST_ACTIONS;

/** The URL parameter in which the app lists the signals it handles. */
export const HOST_ACTIONS_PARAM = "ionHostActions";

export interface IonHostActions {
  /** Whether the app handles this signal (and so the button is shown). */
  can(action: IonHostAction): boolean;
  /** Sends the signal; does not wait for the app's answer. */
  send(action: IonHostAction, data?: Record<string, unknown>): void;
}

export const noIonHostActions: IonHostActions = {
  can: () => false,
  send: () => {},
};

/** The signals listed in the URL (query or fragment query). */
export function parseHostActions(
  search: string,
  hash: string,
): Set<IonHostAction> {
  const fragmentQuery = hash.includes("?") ? hash.slice(hash.indexOf("?")) : "";
  const value =
    new URLSearchParams(fragmentQuery).get(HOST_ACTIONS_PARAM) ??
    new URLSearchParams(search).get(HOST_ACTIONS_PARAM) ??
    "";
  const listed = new Set(value.split(",").map((s) => s.trim()));
  return new Set(
    (Object.keys(HOST_ACTIONS) as IonHostAction[]).filter((a) => listed.has(a)),
  );
}

export function createIonHostActions(
  widget: WidgetHelpers | null,
  search: string = window.location.search,
  hash: string = window.location.hash,
): IonHostActions {
  if (!ION_HOST_ACTIONS || widget === null) return noIonHostActions;
  const handled = parseHostActions(search, hash);
  return {
    can: (action) => handled.has(action),
    send: (action, data = {}): void => {
      if (!handled.has(action)) return;
      widget.api.transport.send(HOST_ACTIONS[action], data).catch((e) =>
        // The app may answer late or with an error (Rust SDK: unknown
        // action); the signal has gone either way
        rootLogger
          .getChild("[ion hostActions]")
          .warn(`No answer to ${HOST_ACTIONS[action]}`, e),
      );
    },
  };
}

const IonHostActionsContext = createContext<IonHostActions>(noIonHostActions);

export const IonHostActionsProvider: FC<{
  value: IonHostActions;
  children: ReactNode;
}> = ({ value, children }) => (
  <IonHostActionsContext value={value}>{children}</IonHostActionsContext>
);

export const useIonHostActions = (): IonHostActions =>
  use(IonHostActionsContext);
